
import React from 'react';
import { Helmet } from 'react-helmet';

type ItineraryDay = {
  id: string;
  day_number: number;
  title: string;
  description?: string | null;
  location?: string | null;
};

type ItineraryTag = {
  id: string;
  name: string;
};

type ItineraryProps = {
  id: string;
  title: string;
  slug?: string | null;
  description?: string | null;
  destination?: string | null;
  duration?: number | null;
  cost_estimate?: number | null;
  best_season?: string | null;
  featured_image?: string | null;
  created_at?: string;
  updated_at?: string;
  itinerary_days?: ItineraryDay[];
  tags?: ItineraryTag[];
};

interface ItineraryStructuredDataProps {
  itinerary: ItineraryProps;
  baseUrl: string;
}

export function ItineraryStructuredData({ itinerary, baseUrl }: ItineraryStructuredDataProps) {
  const itineraryUrl = `${baseUrl}/itineraries/${itinerary.slug || itinerary.id}`;
  
  // Convert itinerary days to Trip itinerary
  const itineraryItems = itinerary.itinerary_days?.map(day => ({
    "@type": "ItemList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": day.day_number,
        "item": {
          "@type": "TouristAttraction",
          "name": day.title,
          "description": day.description || `Day ${day.day_number} of the itinerary`,
          ...(day.location ? { "address": { "@type": "PostalAddress", "addressLocality": day.location } } : {})
        }
      }
    ]
  })) || [];

  // Build the structured data object
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Trip",
    "name": itinerary.title,
    "description": itinerary.description || `A ${itinerary.duration || ''} day itinerary for ${itinerary.destination || 'travel'}`,
    "url": itineraryUrl,
    ...(itinerary.featured_image ? {
      "image": {
        "@type": "ImageObject",
        "url": itinerary.featured_image,
        "width": 1200,
        "height": 630
      }
    } : {}),
    ...(itinerary.destination ? {
      "destination": {
        "@type": "TouristDestination",
        "name": itinerary.destination,
        "description": `Travel guide and itinerary for ${itinerary.destination}`
      }
    } : {}),
    "itinerary": itineraryItems,
    ...(itinerary.best_season ? { "season": itinerary.best_season } : {}),
    ...(itinerary.duration ? { "tripDuration": `P${itinerary.duration}D` } : {}),
    ...(itinerary.cost_estimate ? { 
      "estimatedCost": {
        "@type": "MonetaryAmount",
        "currency": "USD",
        "value": itinerary.cost_estimate
      }
    } : {}),
    "keywords": itinerary.tags?.map(tag => tag.name).join(', ') || itinerary.destination || "travel itinerary"
  };
  
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}
